import Plyr from 'plyr';
import Flickity from 'flickity-fade';
import { sleep } from './lib/utils.js';
import { createGLightbox } from './lib/glightbox';

class AstroModals {
	readonly contactHeadline = 'REQUEST A CUSTOMIZATION, TRAINING OR SUPPORT';
	readonly payloadHeadline = 'REQUEST A CUSTOM PAYLOAD';

	configureModal(trigger: Element | null | undefined, root: ParentNode) {
		const hl = root.querySelector('.js-astroRequestModalHl') as HTMLElement | null;
		const topic = root.querySelector('.js-astroRequestModalTopic') as HTMLSelectElement | null;
		const message = root.querySelector('.js-message') as HTMLTextAreaElement | null;
		if (!hl || !topic) {
			return;
		}

		const isPayload = trigger?.classList.contains('js-astroInlinePayloadModal');
		if (isPayload) {
			hl.textContent = this.payloadHeadline;
			topic.value = 'custom_payload';
			if (message) {
				message.placeholder = 'What payload do you need help with?';
			}
		} else {
			hl.textContent = this.contactHeadline;
			topic.value = '';
			if (message) {
				message.placeholder = 'Message';
			}
		}
	}

	constructor() {
		this.initEvents();
	}

	initEvents() {
		const lightbox = createGLightbox({
			selector: '.js-astroInlineContactModal, .js-astroInlinePayloadModal',
			skin: 'astroInlineModal',
			width: 'auto',
			height: 'auto',
			touchNavigation: false,
			draggable: false,
			loop: false,
		});

		// glightbox passes slideData to slide_before_load; bundled types omit the argument
		lightbox.on('slide_before_load', ((data: { trigger?: Element }) => {
			const original = document.getElementById('js-astroRequestModal');
			if (original) {
				this.configureModal(data.trigger, original);
			}
		}) as () => void);
	}
}
new AstroModals();

class AstroRequests {
	DOM = {} as Record<string, any>;
	activeForm: HTMLFormElement | null = null;
	topicData: Record<string, { prompt: string; subject?: string }> = {
		default: {
			prompt: 'Message',
		},
		astro_customization: {
			prompt: 'What customization do you need?',
			subject: 'Astro customization request',
		},
		astro_training: {
			prompt: 'What specific topics would you like us to cover in your training?',
			subject: 'Astro training request',
		},
		custom_payload: {
			prompt: 'What payload do you need help with?',
			subject: 'Astro custom payload request',
		},
	};

	postAjax(
		url: string,
		data: Record<string, string> | string,
		success: (response: string) => void
	) {
		var params =
			typeof data == 'string'
				? data
				: Object.keys(data)
						.map(function (k) {
							return encodeURIComponent(k) + '=' + encodeURIComponent(data[k as keyof typeof data]);
						})
						.join('&');

		var xhr: XMLHttpRequest = window.XMLHttpRequest
			? new XMLHttpRequest()
			: (new ActiveXObject('Microsoft.XMLHTTP') as XMLHttpRequest);
		xhr.open('POST', url);
		xhr.onreadystatechange = function () {
			if (xhr.readyState > 3 && xhr.status == 200) {
				success(xhr.responseText);
			}
		};
		xhr.setRequestHeader('X-Requested-With', 'XMLHttpRequest');
		xhr.setRequestHeader('Content-Type', 'application/x-www-form-urlencoded');
		xhr.send(params);
		return xhr;
	}

	constructor() {
		this.DOM = {
			form: document.querySelector('.js-astroRequestForm'),
		};
		if (this.DOM.form) {
			this.DOM.topic = this.DOM.form.querySelector('.js-topic');
			this.DOM.message = this.DOM.form.querySelector('.js-message');
			this.DOM.submit = this.DOM.form.querySelector('.js-submit');
			this.DOM.inputs = this.DOM.form.querySelectorAll('.js-input');
			this.DOM.footer = this.DOM.form.querySelector('.js-footer');
			this.initEvents();
		}
	}

	initEvents() {
		document.addEventListener('submit', (e) => {
			const form = (e.target as HTMLElement | null)?.closest?.('.js-astroRequestForm');
			if (form instanceof HTMLFormElement) {
				this.submit(e, form);
			}
		});
		document.addEventListener('change', (e) => {
			const select = e.target as HTMLSelectElement;
			if (select.matches('.js-astroRequestForm .js-topic')) {
				this.topicChange(select);
			}
		});
	}

	topicChange(select: HTMLSelectElement) {
		const form = select.closest('.js-astroRequestForm');
		const message = form?.querySelector('.js-message') as HTMLTextAreaElement | null;
		if (!message) {
			return;
		}
		const val = select.value;
		const prompt = val && val !== '' ? this.topicData[val].prompt : this.topicData.default.prompt;
		message.placeholder = prompt;
	}

	getData(form: HTMLFormElement) {
		var data = {};
		form.querySelectorAll('.js-input').forEach((el) => {
			const input = el as HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement;
			if (input.value === '') return;
			data[input.name] = input.value;
		});

		data['user_system'] = 'Astro';
		const topic = data['user_topic'];
		data['user_subject'] = this.topicData[topic].subject;

		return data;
	}

	submit(e: Event, form: HTMLFormElement) {
		e.preventDefault();
		this.activeForm = form;
		const submitButton = form.querySelector('.js-submit') as HTMLButtonElement;
		submitButton.setAttribute('disabled', 'true');

		var data = this.getData(form);

		grecaptcha.ready(() => {
			grecaptcha
				.execute(RECAPTCHA_SITE_KEY, { action: 'submit' })
				.then((token) => {
					data['g-recaptcha-response'] = token;
					data['recaptchaVersion'] = 3;
					const ajaxObject = {
						action: 'contact_submission',
						data: JSON.stringify(data),
					};
					this.postAjax(ajaxurl, ajaxObject, this.submissionResponse.bind(this));
				})
				.catch(() => {
					const footer = form.querySelector('.js-footer');
					const messageEl = document.createElement('p');
					messageEl.classList.add('error', 'responseMessage');
					messageEl.innerHTML =
						'Uh oh! There was an error verifying your submission. Please try again.';
					footer?.insertBefore(messageEl, submitButton);
					submitButton.removeAttribute('disabled');
				});
		});
	}

	submissionResponse(res) {
		const form = this.activeForm;
		if (!form) {
			return;
		}
		const footer = form.querySelector('.js-footer');
		const submitButton = form.querySelector('.js-submit') as HTMLButtonElement;
		const message = form.querySelector('.js-message') as HTMLTextAreaElement;
		const inputs = form.querySelectorAll('.js-input');

		res = JSON.parse(res);
		if (res.hasOwnProperty('errors')) {
			const messageEl = document.createElement('p');
			messageEl.classList.add('error', 'responseMessage');
			messageEl.innerHTML = `Uh oh! There was an error: ${res.errors}`;
			footer?.insertBefore(messageEl, submitButton);
			submitButton.removeAttribute('disabled');
		} else {
			const messageEl = document.createElement('p');
			messageEl.classList.add('success', 'responseMessage');
			messageEl.innerHTML = "Bingo! We'll be in touch.";
			footer?.insertBefore(messageEl, submitButton);
			for (var i = 0; i < inputs.length; i++) {
				(inputs[i] as HTMLInputElement).value = '';
			}
			message.placeholder = this.topicData.default.prompt;
		}
	}
}
new AstroRequests();

class AstroPayloads {
	detailsOpen = false;
	DOM = {} as Record<string, any>;
	flkty: FlickityInstance;

	constructor() {
		this.DOM = {
			// section: document.querySelector(".js-astroPayloadBlade"),
			list: document.querySelector('.js-astroPayloadsList'),
			ui: document.querySelector('.js-astroPayloadsUI'),
			details: document.querySelector('.js-astroPayloadDetails'),
			wrapper: document.querySelector('.js-astroPayloadDetailPadding'),
			close: document.querySelector('.js-astroCloseDetails'),
			learnLinks: document.querySelectorAll('.js-payloadLearnLink'),
			rotator: document.querySelector('.js-astroPayloadDetailsRotator'),
			videoTabLinks: document.querySelectorAll('.js-astroPayloadVideoLink'),
			prevButton: document.querySelector('.js-astroPayloadPrevious'),
			nextButton: document.querySelector('.js-astroPayloadNext'),
		};
		if (!this.DOM.list) return;
		this.events();
		this.initRotator();
	}

	events() {
		this.DOM.close.addEventListener('click', this.closeClick.bind(this));
		for (let i = 0; i < this.DOM.learnLinks.length; i++) {
			this.DOM.learnLinks[i].addEventListener('click', this.learnLinkClick.bind(this));
		}
		for (let i = 0; i < this.DOM.videoTabLinks.length; i++) {
			this.DOM.videoTabLinks[i].addEventListener('click', this.videoTabLinkClick.bind(this));
		}
	}

	videoTabLinkClick(e) {
		const link = e.currentTarget;
		const panel = link.closest('.js-astroPayloadVideoTabs');
		const videos = panel.querySelectorAll('.js-astroPayloadVideo');
		const links = panel.querySelectorAll('.js-astroPayloadVideoLink');
		const index = Array.prototype.indexOf.call(links, link);
		for (let i = 0; i < links.length; i++) {
			if (i === index) {
				videos[i].classList.add('js-active');
				links[i].classList.add('js-active');
			} else {
				videos[i].classList.remove('js-active');
				links[i].classList.remove('js-active');
			}
		}
	}

	initRotator() {
		if (!this.DOM.rotator) return;
		this.flkty = new Flickity(this.DOM.rotator, {
			// options
			cellAlign: 'left',
			contain: true,
			pageDots: false,
			prevNextButtons: false,
			wrapAround: true,
			imagesLoaded: true,
		}) as unknown as FlickityInstance;
		this.DOM.prevButton.addEventListener('click', this.prevClick.bind(this));
		this.DOM.nextButton.addEventListener('click', this.nextClick.bind(this));
	}
	prevClick() {
		this.flkty.previous(true);
	}
	nextClick() {
		this.flkty.next(true);
	}

	async learnLinkClick(e) {
		const link = e.currentTarget;
		const index = Array.prototype.indexOf.call(this.DOM.learnLinks, link);
		this.flkty.select(index);

		this.DOM.ui.style.height = `${this.DOM.ui.offsetHeight}px`;
		this.DOM.list.style.opacity = 0;
		this.DOM.list.style.position = 'absolute';
		this.DOM.list.style.pointerEvents = 'none';

		await sleep(10);

		const targetHeight = this.DOM.details.offsetHeight;
		this.DOM.ui.style.height = `${targetHeight}px`;
		this.DOM.details.style.opacity = 1;
		this.DOM.details.style.position = 'relative';
		this.DOM.details.style.pointerEvents = 'auto';
		await sleep(300);

		this.DOM.ui.style.height = null;
	}

	async closeClick() {
		this.DOM.ui.style.height = `${this.DOM.ui.offsetHeight}px`;
		this.DOM.details.style.opacity = 0;
		this.DOM.details.style.position = 'absolute';
		this.DOM.details.style.pointerEvents = 'none';

		await sleep(10);

		const targetHeight = this.DOM.details.offsetHeight;
		this.DOM.ui.style.height = `${targetHeight}px`;
		this.DOM.list.style.opacity = 1;
		this.DOM.list.style.position = 'relative';
		this.DOM.list.style.pointerEvents = 'auto';

		await sleep(300);

		this.DOM.ui.style.height = null;
	}
}
new AstroPayloads();

class AstroEcosystem {
	$ecosystem: Element | null = null;
	ecosystemThreshold = 0;
	ecosystemTriggered = false;

	constructor() {
		var self = this;
		this.cache();
		this.events();
		this.relativeVariables();
		setInterval(() => self.relativeVariables(), 3000);
	}

	//utility
	offset(el) {
		var rect = el.getBoundingClientRect(),
			scrollLeft = window.pageXOffset || document.documentElement.scrollLeft,
			scrollTop = window.pageYOffset || document.documentElement.scrollTop;
		return { top: rect.top + scrollTop, left: rect.left + scrollLeft };
	}

	cache() {
		this.$ecosystem = document.querySelector('.astroEcosystem');
	}

	events() {
		if (this.$ecosystem) {
			document.addEventListener('optimizedScroll', this.ecosystemScroll.bind(this));
			document.addEventListener('optimizedResize', this.relativeVariables.bind(this));
		}
	}

	relativeVariables() {
		if (!this.$ecosystem) return;
		var ecosystemOffset = this.offset(this.$ecosystem);
		this.ecosystemThreshold = ecosystemOffset.top; // - Math.floor( .25 * window.innerHeight );
	}

	ecosystemScroll() {
		var scrollPosition = window.pageYOffset || document.documentElement.scrollTop;

		if (scrollPosition > this.ecosystemThreshold && !this.ecosystemTriggered) {
			this.$ecosystem.classList.add('active');
			this.ecosystemTriggered = true;
		} else if (scrollPosition < this.ecosystemThreshold && this.ecosystemTriggered) {
			this.$ecosystem.classList.remove('active');
			this.ecosystemTriggered = false;
		}
	}
}
const _astroEcosystem = new AstroEcosystem();

class FFTabRotator extends HTMLElement {
	DOM = {} as Record<string, any>;
	flkty: FlickityInstance;

	constructor() {
		super();
		this.cache();
		if (!this.DOM.tabs.length) return;
		this.initRotator();
		this.initControls();
		this.events();
	}

	cache() {
		this.DOM = {
			tabs: this.querySelectorAll('.js-tabControl'),
			indicator: this.querySelector('.js-tabIndicator'),
			rotator: this.querySelector('.js-tabSlides'),
		};
	}

	initControls() {
		this.DOM.tabs[0]?.classList.add('js-active');
	}

	initRotator() {
		if (!this.DOM.rotator) return;
		this.flkty = new Flickity(this.DOM.rotator, {
			pageDots: false,
			prevNextButtons: false,
			fade: true,
			imagesLoaded: true,
		}) as unknown as FlickityInstance;
		this.flkty.on('change', this.rotatorSettle.bind(this));
	}

	events() {
		for (let i = 0; i < this.DOM.tabs.length; i++) {
			this.DOM.tabs[i].addEventListener('click', this.tabClick.bind(this, i));
		}
	}

	tabClick(i, _e) {
		this.flkty.select(i);
	}

	rotatorSettle(index) {
		for (let i = 0; i < this.DOM.tabs.length; i++) {
			this.DOM.tabs[i].classList.remove('js-active');
		}
		this.DOM.tabs[index].classList.add('js-active');
		this.DOM.indicator.style.transform = `translateX(${index * 100}%)`;
	}
}
customElements.define('ff-tab-rotator', FFTabRotator);

class AstroPayloadRotator {
	DOM = {} as Record<string, any>;
	flkty: FlickityInstance;

	constructor() {
		this.cache();
		if (!this.DOM) return;
		this.init();
		this.events();
	}

	cache() {
		const wrapper = document.querySelector('.js-astroPayloadsRotator');
		if (!wrapper) return;
		this.DOM = {
			rotator: wrapper.querySelector('.rotator'),
			next: wrapper.querySelector('.js-next'),
			prev: wrapper.querySelector('.js-prev'),
		};
	}

	init() {
		if (!this.DOM.rotator) return;
		this.flkty = new Flickity(this.DOM.rotator, {
			cellAlign: 'left',
			contain: true,
			pageDots: false,
			wrapAround: true,
			prevNextButtons: false,
			imagesLoaded: true,
		}) as unknown as FlickityInstance;
	}
	events() {
		this.DOM.next.addEventListener('click', this.nextClick.bind(this));
		this.DOM.prev.addEventListener('click', this.prevClick.bind(this));
	}

	prevClick() {
		this.flkty.previous(true);
	}
	nextClick() {
		this.flkty.next(true);
	}
}
new AstroPayloadRotator();

class FFGallery extends HTMLElement {
	hovering = false;
	index = 0;
	hoverIndex = 0;
	players: GalleryPlayerEntry[] = [];
	playingVideo = 0;
	data: GallerySlideData[] = [];
	DOM = {} as Record<string, any>;
	flkty: FlickityInstance;

	constructor() {
		super();
		this.data = JSON.parse(galleryData);
		this.cache();
		if (!this.DOM.prev || !this.DOM.next || !this.DOM.controlPanel || !this.DOM.rotator) {
			return;
		}
		this.events();
		this.initVideos();
		this.initRotator();
	}

	cache() {
		this.DOM = {
			next: this.querySelector('.js-next'),
			prev: this.querySelector('.js-prev'),
			controlPanel: this.querySelector('.js-controlPanel'),
			dots: this.querySelectorAll('.js-dot'),
			hoverTitle: this.querySelector('.js-hoverTitle'),
			rotator: this.querySelector('.js-rotator'),
			slides: this.querySelectorAll('.js-slide'),
		};
	}

	events() {
		this.DOM.prev.addEventListener('click', this.prevClick.bind(this));
		this.DOM.next.addEventListener('click', this.nextClick.bind(this));
		for (let i = 0; i < this.DOM.dots.length; i++) {
			this.DOM.dots[i].addEventListener('click', this.dotClick.bind(this, i));
			this.DOM.dots[i].addEventListener('mouseenter', this.dotEnter.bind(this, i));
		}
		this.DOM.controlPanel.addEventListener('mouseenter', this.controlEnter.bind(this));
		this.DOM.controlPanel.addEventListener('mouseleave', this.controlLeave.bind(this));
	}

	initVideos() {
		for (let i = 0; i < this.DOM.slides.length; i++) {
			const el = this.DOM.slides[i].querySelector('.js-video');
			let plyr;
			if (el) {
				plyr = new Plyr(el, {
					controls: ['play'],
					loop: { active: true },
					iconUrl: `${templateDirectoryUri}/partials/ember/plyr.svg`,
				});
				if (i === 0) {
					plyr.muted = true; //this is weirdly needed
				} else {
					plyr.muted = false;
				}
			}
			this.players.push({
				plyr: plyr,
			});
		}
	}

	initRotator() {
		if (!this.DOM.rotator) return;
		this.flkty = new Flickity(this.DOM.rotator, {
			pageDots: false,
			prevNextButtons: false,
			imagesLoaded: true,
		}) as unknown as FlickityInstance;
		this.flkty.on('change', this.rotatorChange.bind(this));
	}

	prevClick() {
		this.flkty.previous();
	}
	nextClick() {
		this.flkty.next();
	}
	dotClick(i) {
		this.flkty.select(i);
	}
	dotEnter(index) {
		this.hoverIndex = index;
		for (let i = 0; i < this.DOM.dots.length; i++) {
			this.DOM.dots[i].classList.remove('js-active');
		}
		this.DOM.dots[index].classList.add('js-active');
		this.DOM.hoverTitle.innerHTML = this.data[index].title;
	}
	controlEnter() {
		this.hovering = true;
	}
	controlLeave() {
		this.hovering = false;
		for (let i = 0; i < this.DOM.dots.length; i++) {
			this.DOM.dots[i].classList.remove('js-active');
		}
		this.DOM.dots[this.index].classList.add('js-active');
		this.DOM.hoverTitle.innerHTML = this.data[this.index].title;
	}
	rotatorChange(index) {
		this.index = index;
		for (let i = 0; i < this.DOM.dots.length; i++) {
			this.DOM.dots[i].classList.remove('js-active');
		}
		if (index === this.DOM.dots.length - 1) {
			this.DOM.next.disabled = true;
		} else {
			this.DOM.next.disabled = false;
		}
		if (index === 0) {
			this.DOM.prev.disabled = true;
		} else {
			this.DOM.prev.disabled = false;
		}
		if (this.players[this.playingVideo].plyr) {
			this.players[this.playingVideo].plyr.pause();
		}
		if (this.players[index].plyr) {
			void Promise.resolve(this.players[index].plyr.play()).catch(() => {});
		}
		this.playingVideo = index;

		if (this.hovering) return;
		this.DOM.dots[index].classList.add('js-active');
		this.DOM.hoverTitle.innerHTML = this.data[index].title;
	}
}
customElements.define('ff-gallery', FFGallery);

class AstroImaging {
	currentIndex = 0;
	DOM = {} as Record<string, any>;
	flkty: FlickityInstance;

	constructor() {
		this.cache();
		this.initRotator();
		this.events();
	}

	cache() {
		this.DOM = {
			rotator: document.querySelector('.js-astroImaging .js-rotator'),
			controls: document.querySelectorAll('.js-astroImaging .js-controlButton'),
		};
	}

	initRotator() {
		if (!this.DOM.rotator) return;
		this.flkty = new Flickity(this.DOM.rotator, {
			pageDots: false,
			prevNextButtons: false,
			fade: true,
			imagesLoaded: true,
		}) as unknown as FlickityInstance;
		this.flkty.on('change', this.rotatorChange.bind(this));
		const resize = () => {
			this.ensureImagingViewportHeight();
			this.flkty.resize();
		};
		this.ensureImagingViewportHeight();
		this.DOM.rotator.querySelectorAll('img').forEach((img) => {
			if (img.complete) return;
			img.addEventListener('load', resize, { once: true });
		});
		window.addEventListener('load', resize, { once: true });
		requestAnimationFrame(() => requestAnimationFrame(resize));
	}

	/** Fade cells are measured absolute; if viewport height was 0, Flickity leaves it stuck at 0px. */
	ensureImagingViewportHeight() {
		const rotator = this.DOM.rotator;
		if (!rotator) return;
		const vp = rotator.querySelector('.flickity-viewport');
		if (!vp) return;
		const w = rotator.getBoundingClientRect().width;
		if (w < 1) return;
		const targetH = Math.round((w * 776) / 740);
		const h = vp.getBoundingClientRect().height;
		if (h < 2) {
			vp.style.height = `${targetH}px`;
		}
	}

	events() {
		for (let i = 0; i < this.DOM.controls.length; i++) {
			this.DOM.controls[i].addEventListener('click', this.buttonClick.bind(this));
		}
	}

	buttonClick(e) {
		const targ = e.currentTarget;
		let i = [...this.DOM.controls].indexOf(targ);
		i = i === this.currentIndex ? 0 : i;
		this.flkty.select(i);
		this.currentIndex = i;
	}

	rotatorChange(i) {
		for (let i = 0; i < this.DOM.controls.length; i++) {
			this.DOM.controls[i].classList.remove('active');
		}
		this.DOM.controls[i].classList.add('active');
	}
}
new AstroImaging();
